<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import { ArrowRight, CheckCircle2, Landmark, LayoutDashboard, RefreshCw, Store } from 'lucide-vue-next'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { Badge, Button, Card } from '@/components/ui'
import { track } from '@/lib/usageTracker'
import { setupFlowSteps } from './setupFlow'

const auth = useAuthStore()
const router = useRouter()

const alias = computed(() => (auth.verification?.account as { alias?: string })?.alias || 'Accounting Account')
const providerName = computed(() => auth.selectedPackageId || 'Accounting provider')
const connectedAt = computed(() => {
  const integration = auth.currentIntegration
  if (!integration?.connectedAt) return 'Just now'
  try {
    return new Intl.DateTimeFormat('en-ZM', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(integration.connectedAt))
  } catch {
    return integration.connectedAt
  }
})
const contextItems = computed(() => [
  { label: 'Store reference', value: auth.storeID || 'Pending', tone: 'success' },
  { label: 'Invoice reference', value: auth.invoiceNumber || 'Pending', tone: 'success' },
  { label: 'Provider', value: providerName.value, tone: 'accent' },
  { label: 'Connection state', value: 'Connected and ready', tone: 'success' },
])

function goToDashboard() {
  track('btn:GoToDashboard', 'click')
  router.push({ name: 'Dashboard' })
}
</script>

<template>
  <SetupFlowShell
    eyebrow="Connection complete"
    title="Accounting connection is ready"
    subtitle="Your provider, store, and invoice references are now linked so you can move into the operational workspace."
    :current-step="4"
    :steps="setupFlowSteps"
    :provider-name="providerName"
    :context-items="contextItems"
  >
    <Card padding="lg" variant="connected" class="success-panel">
      <div class="success-panel__status">
        <div class="success-panel__mark">
          <CheckCircle2 class="h-6 w-6" />
        </div>
        <Badge variant="success">Connected</Badge>
      </div>

      <p class="success-panel__copy">
        Store <strong>{{ auth.storeID }}</strong> is now connected to <strong>{{ providerName }}</strong>. The workspace can begin checking status and syncing accounting records.
      </p>

      <div class="success-panel__receipt">
        <div class="receipt-row">
          <span class="receipt-label"><Store class="h-4 w-4" /> Store reference</span>
          <span class="receipt-value">{{ auth.storeID || '—' }}</span>
        </div>
        <div class="receipt-row">
          <span class="receipt-label"><Landmark class="h-4 w-4" /> Provider</span>
          <span class="receipt-value">{{ providerName }}</span>
        </div>
        <div class="receipt-row">
          <span class="receipt-label"><RefreshCw class="h-4 w-4" /> Connected at</span>
          <span class="receipt-value">{{ connectedAt }}</span>
        </div>
      </div>

      <div class="success-panel__actions">
        <Button @click="goToDashboard">
          Go to Dashboard
          <ArrowRight class="h-4 w-4" />
        </Button>
      </div>
    </Card>

    <Card padding="lg" class="next-actions">
      <div class="next-actions__eyebrow">Next actions</div>
      <div class="next-actions__list">
        <div class="next-action">
          <LayoutDashboard class="h-4 w-4" />
          <div>
            <div class="next-action__title">Review connection health</div>
            <p class="next-action__copy">Open the dashboard to confirm provider status, company context, and current sync signals.</p>
          </div>
        </div>
        <div class="next-action">
          <RefreshCw class="h-4 w-4" />
          <div>
            <div class="next-action__title">Run your first sync check</div>
            <p class="next-action__copy">Use the operational cards to inspect queue counts and refresh the entities that matter first.</p>
          </div>
        </div>
      </div>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.success-panel,
.next-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.success-panel__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.success-panel__mark {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: var(--radius-md);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.12);
  color: #0f8f69;
  flex-shrink: 0;
}

.receipt-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.receipt-label,
.next-actions__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.next-action__title {
  margin: 0.2rem 0 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--color-ink);
}

.success-panel__copy,
.next-action__copy {
  margin: 0;
  color: var(--color-ink-soft);
  font-size: 0.9rem;
}

.success-panel__receipt {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  border-top: 1px solid var(--color-border-soft);
  border-bottom: 1px solid var(--color-border-soft);
  padding: 0.9rem 0;
}

.receipt-label {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.receipt-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-ink);
  text-align: right;
}

.next-actions__list {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.next-action {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface-muted);
  padding: 0.8rem;
}

.next-action svg {
  color: var(--color-ink-soft);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

@media (max-width: 640px) {
  .success-panel,
  .next-actions {
    gap: 0.8rem;
  }

  .receipt-row {
    flex-direction: column;
  }

  .success-panel__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }

  .next-action {
    padding: 0.7rem;
  }

  .receipt-value {
    text-align: left;
  }
}
</style>
